import {
  collection,
  doc,
  getDocs,
  getDocsFromServer,
  runTransaction,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Level, LevelType } from '../types/Level';
import { LevelExistsError } from '../studio/publish';

const COLLECTION_NAME = 'levels';

// Convert Firestore document to Level interface (matching Unity structure exactly)
const convertFirestoreToLevel = (doc: QueryDocumentSnapshot<DocumentData>): Level => {
  const data = doc.data();
  const level: Level = {
    id: doc.id, // Document ID is the level number
    levelType: data.levelType as LevelType || data.levelTypeString as LevelType,
    difficulty: data.difficulty || 1
  };

  // Only add fields that exist and are relevant to the level type
  if (data.words && data.words.length > 0) level.words = data.words;
  if (data.keyLetters && data.keyLetters.length > 0) level.keyLetters = data.keyLetters;
  if (data.category) level.category = data.category;
  if (data.sentence) level.sentence = data.sentence;
  if (data.locationName) level.locationName = data.locationName;
  if (data.image) level.image = data.image;
  if (data.funFact) level.funFact = data.funFact;

  // Handle new WordSearch grid structure
  if (data.wordSearchGrid) {
    level.wordSearchGrid = {
      width: data.wordSearchGrid.width,
      height: data.wordSearchGrid.height,
      // Convert Unity's GridRow format back to 2D array
      grid: data.wordSearchGrid.grid.map((gridRow: any) => gridRow.row || gridRow),
      wordPositions: data.wordSearchGrid.wordPositions
    };
  }

  return level;
};

// Convert Level to Firestore document format (matching Unity structure exactly)
const convertLevelToFirestore = (level: Omit<Level, 'id'>) => {
  const firestoreData: any = {
    levelType: level.levelType.toString(), // Unity expects levelType field
    difficulty: level.difficulty,
    funFact: level.funFact || ""
  };

  // Only add fields that are relevant to the specific level type
  switch (level.levelType) {
    case LevelType.CategoryWords:
      if (level.words && level.words.length > 0) firestoreData.words = level.words;
      if (level.keyLetters && level.keyLetters.length > 0) firestoreData.keyLetters = level.keyLetters;
      if (level.category) firestoreData.category = level.category;
      break;

    case LevelType.KnownSentence:
    case LevelType.WhatsInThePicture:
    case LevelType.WhatsTheSong:
      if (level.sentence) firestoreData.sentence = level.sentence;
      if (level.keyLetters && level.keyLetters.length > 0) firestoreData.keyLetters = level.keyLetters;
      if (level.image) firestoreData.image = level.image;
      break;

    case LevelType.WhereIsThisLocated:
      if (level.sentence) firestoreData.sentence = level.sentence;
      if (level.locationName) firestoreData.locationName = level.locationName;
      if (level.keyLetters && level.keyLetters.length > 0) firestoreData.keyLetters = level.keyLetters;
      if (level.image) firestoreData.image = level.image;
      break;

    case LevelType.WordSearch:
      // New grid-based approach
      if (level.wordSearchGrid) {
        firestoreData.wordSearchGrid = {
          width: level.wordSearchGrid.width,
          height: level.wordSearchGrid.height,
          grid: level.wordSearchGrid.grid.map(row => ({ row: row })), // Convert to Unity's GridRow format
          wordPositions: level.wordSearchGrid.wordPositions
        };
      }
      // Keep old words array for backward compatibility
      if (level.words && level.words.length > 0) firestoreData.words = level.words;
      if (level.category) firestoreData.category = level.category;
      break;
  }

  // Always add lastModified timestamp (Unix seconds)
  firestoreData.lastModified = Math.floor(Date.now() / 1000);

  return firestoreData;
};

export const firebaseService = {
  // Get all levels (ordered by document ID numerically)
  async getLevels(): Promise<Level[]> {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
      const levels = querySnapshot.docs.map(convertFirestoreToLevel);

      // Sort by numeric ID (document ID)
      return levels.sort((a, b) => {
        const numA = parseInt(a.id) || 0;
        const numB = parseInt(b.id) || 0;
        return numA - numB;
      });
    } catch (error) {
      console.error('Error fetching levels:', error);
      throw new Error('Failed to fetch levels from Firebase');
    }
  },

  // Reads the level list from the server only: throws when offline instead of returning a cached or empty list
  async getLevelsFromServer(): Promise<Level[]> {
    const querySnapshot = await getDocsFromServer(collection(db, COLLECTION_NAME));
    return querySnapshot.docs.map(convertFirestoreToLevel);
  },

  // Creates a level only when that number is free; never overwrites and never falls back to local storage
  async createLevelIfAbsent(id: string, levelData: Omit<Level, 'id'>): Promise<Level> {
    const docRef = doc(db, COLLECTION_NAME, id);
    await runTransaction(db, async transaction => {
      const existing = await transaction.get(docRef);
      if (existing.exists()) throw new LevelExistsError(id);
      transaction.set(docRef, convertLevelToFirestore(levelData));
    });
    return { ...levelData, id };
  },

  // Get level by ID
  async getLevelById(id: string): Promise<Level | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return convertFirestoreToLevel(docSnap as QueryDocumentSnapshot<DocumentData>);
      }
      return null;
    } catch (error) {
      console.error('Error fetching level:', error);
      throw new Error(`Failed to fetch level ${id} from Firebase`);
    }
  },

  // Add new level with next numeric ID
  async addLevel(levelData: Omit<Level, 'id'>): Promise<Level> {
    try {
      // Get all existing levels to find the highest ID
      const existingLevels = await this.getLevels();
      let nextId = 1;

      if (existingLevels.length > 0) {
        const highestId = Math.max(...existingLevels.map(level => parseInt(level.id) || 0));
        nextId = highestId + 1;
      }

      const newId = nextId.toString();
      const firestoreData = convertLevelToFirestore(levelData);

      // Use setDoc with the numeric ID instead of addDoc
      const docRef = doc(db, COLLECTION_NAME, newId);
      await setDoc(docRef, firestoreData);

      // Return the created level with the numeric ID
      return {
        ...levelData,
        id: newId
      };
    } catch (error) {
      console.error('Error adding level:', error);
      throw new Error('Failed to add level to Firebase');
    }
  },

  // Add new level with specific ID
  async addLevelWithId(id: string, levelData: Omit<Level, 'id'>): Promise<Level> {
    try {
      const firestoreData = convertLevelToFirestore(levelData);

      // Use setDoc with the specified ID
      const docRef = doc(db, COLLECTION_NAME, id);
      await setDoc(docRef, firestoreData);

      // Return the created level with the specified ID
      return {
        ...levelData,
        id
      };
    } catch (error) {
      console.error('Error adding level with ID:', error);
      throw new Error(`Failed to add level ${id} to Firebase`);
    }
  },

  // Update existing level
  async updateLevel(id: string, updates: Partial<Level>): Promise<Level> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);

      // Get the existing level to merge with updates
      const existingLevel = await this.getLevelById(id);
      if (!existingLevel) {
        throw new Error(`Level ${id} not found`);
      }

      // Merge existing level with updates
      const updatedLevel = { ...existingLevel, ...updates };
      const firestoreUpdates = convertLevelToFirestore(updatedLevel);

      await updateDoc(docRef, firestoreUpdates);

      // Fetch and return the updated document
      const result = await this.getLevelById(id);
      if (!result) {
        throw new Error('Failed to fetch updated level');
      }

      return result;
    } catch (error) {
      console.error('Error updating level:', error);
      throw new Error(`Failed to update level ${id} in Firebase`);
    }
  },

  // Delete level
  async deleteLevel(id: string): Promise<boolean> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting level:', error);
      throw new Error(`Failed to delete level ${id} from Firebase`);
    }
  },

  // Export all levels as JSON
  async exportLevelsAsJSON(): Promise<string> {
    try {
      const levels = await this.getLevels();
      return JSON.stringify(levels, null, 2);
    } catch (error) {
      console.error('Error exporting levels:', error);
      throw new Error('Failed to export levels from Firebase');
    }
  }
};

// Highscores Service
export interface HighscoreEntry {
  id: string; // userId
  name: string;
  level: number;
  userId: string;
  lastModified?: Date; // Timestamp of last update for same-level ordering
}

const HIGHSCORES_COLLECTION = 'Highscores';

export const highscoresService = {
  // Get all highscore entries, sorted by level (desc) then timestamp (asc)
  async getHighscores(): Promise<HighscoreEntry[]> {
    try {
      const querySnapshot = await getDocs(collection(db, HIGHSCORES_COLLECTION));
      const entries = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: doc.id,
          name: data.name || 'Unknown',
          level: data.level || 0,
          lastModified: data.lastModified ? (data.lastModified as Timestamp).toDate() : undefined
        };
      });

      // Sort: highest level first, then earliest timestamp first
      return entries.sort((a, b) => {
        if (b.level !== a.level) {
          return b.level - a.level; // Descending by level
        }
        // Same level: sort by timestamp (earliest first)
        if (!a.lastModified) return 1; // No timestamp goes last
        if (!b.lastModified) return -1;
        return a.lastModified.getTime() - b.lastModified.getTime(); // Ascending by timestamp
      });
    } catch (error) {
      console.error('Error fetching highscores:', error);
      throw new Error('Failed to fetch highscores');
    }
  },

  // Get a single highscore entry
  async getHighscoreById(userId: string): Promise<HighscoreEntry | null> {
    try {
      const docRef = doc(db, HIGHSCORES_COLLECTION, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as HighscoreEntry;
      }
      return null;
    } catch (error) {
      console.error('Error fetching highscore:', error);
      throw new Error(`Failed to fetch highscore for user ${userId}`);
    }
  },

  // Update an existing highscore entry with server timestamp
  async updateHighscore(userId: string, updates: Partial<Omit<HighscoreEntry, 'id' | 'userId' | 'lastModified'>>): Promise<void> {
    try {
      const docRef = doc(db, HIGHSCORES_COLLECTION, userId);
      await updateDoc(docRef, {
        ...updates,
        lastModified: serverTimestamp() // Always update timestamp
      });
    } catch (error) {
      console.error('Error updating highscore:', error);
      throw new Error(`Failed to update highscore for user ${userId}`);
    }
  },

  // Delete a highscore entry
  async deleteHighscore(userId: string): Promise<void> {
    try {
      const docRef = doc(db, HIGHSCORES_COLLECTION, userId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting highscore:', error);
      throw new Error(`Failed to delete highscore for user ${userId}`);
    }
  },

  // Create a new highscore entry with server timestamp
  async createHighscore(userId: string, data: Omit<HighscoreEntry, 'id' | 'lastModified'>): Promise<void> {
    try {
      const docRef = doc(db, HIGHSCORES_COLLECTION, userId);
      await setDoc(docRef, {
        name: data.name,
        level: data.level,
        userId: data.userId,
        lastModified: serverTimestamp() // Set server timestamp
      });
    } catch (error) {
      console.error('Error creating highscore:', error);
      throw new Error(`Failed to create highscore for user ${userId}`);
    }
  },

  // Rebuild highscores from scratch by scanning all users
  async rebuildHighscores(): Promise<void> {
    try {
      // 1. Get all users from Users collection
      const usersSnapshot = await getDocs(collection(db, 'Users'));

      // 2. Extract users with valid data
      const users = usersSnapshot.docs
        .map(doc => ({
          userId: doc.id,
          name: doc.data().name || 'Unknown User',
          level: doc.data().currentLevel || 0
        }))
        .filter(user => user.level > 0); // Only users who completed at least 1 level

      // 3. Sort by level descending and take top 10
      const top10 = users
        .sort((a, b) => b.level - a.level)
        .slice(0, 10);

      // 4. Clear existing highscores collection
      const highscoresSnapshot = await getDocs(collection(db, HIGHSCORES_COLLECTION));
      const deletePromises = highscoresSnapshot.docs.map(doc =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // 5. Add top 10 to highscores collection with server timestamps
      const addPromises = top10.map(user =>
        setDoc(doc(db, HIGHSCORES_COLLECTION, user.userId), {
          name: user.name,
          level: user.level,
          userId: user.userId,
          lastModified: serverTimestamp() // Add server timestamp
        })
      );
      await Promise.all(addPromises);

      console.log(`Rebuilt highscores with ${top10.length} entries`);
    } catch (error) {
      console.error('Error rebuilding highscores:', error);
      throw error;
    }
  }
};

// AI Suggestions Service
export type IdeaCategory = 'songs' | 'sentences' | 'categories' | 'objects' | 'locations';

export interface AISuggestionsData {
  songs?: string;
  sentences?: string;
  categories?: string;
  objects?: string;
  locations?: string;
  providers?: Partial<Record<IdeaCategory, 'claude' | 'gemini' | 'pollinations'>>;
  lastUpdated?: any;
}

export const aiSuggestionsService = {
  async getSuggestions(): Promise<AISuggestionsData | null> {
    try {
      const docRef = doc(db, 'ai_suggestions', 'global');
      const snap = await getDoc(docRef);
      return snap.exists() ? snap.data() as AISuggestionsData : null;
    } catch (error) {
      console.warn('Firebase AI suggestions read failed, falling back to localStorage:', error);
      const localData = localStorage.getItem('ai_suggestions_fallback');
      if (localData) {
        try { return JSON.parse(localData); } catch (e) {}
      }
      return null;
    }
  },
  
  async updateSuggestions(updates: Partial<AISuggestionsData>): Promise<void> {
    // Always update localStorage as backup
    try {
      const localDataStr = localStorage.getItem('ai_suggestions_fallback');
      const currentData = localDataStr ? JSON.parse(localDataStr) : {};
      const newData = {
        ...currentData,
        ...updates,
        providers: { ...currentData.providers, ...updates.providers },
        lastUpdated: Date.now()
      };
      localStorage.setItem('ai_suggestions_fallback', JSON.stringify(newData));
    } catch (localError) {
      console.warn('Failed to update localStorage backup:', localError);
    }

    try {
      const docRef = doc(db, 'ai_suggestions', 'global');
      await setDoc(docRef, { ...updates, lastUpdated: serverTimestamp() }, { merge: true });
    } catch (error) {
      console.warn('Firebase AI suggestions write failed (likely permissions), using localStorage fallback.', error);
    }
  }
};

export default firebaseService;
